import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import { useConfirm } from '../components/ConfirmDialog';

// صور الكاروسيل — الصور فوق بعض (عمودياً)، لكل صورة الترتيب واضح
// جنبها، وأزرار ↑/↓ لتحريكها. زرار واحد لرفع صورة أو دفعة صور —
// المتصفح بيسأل عن ملف واحد أو أكتر لأن الـ input متعلم بـ multiple.

type HeroSlide = {
  id: string;
  image_url: string;
  active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

const ALLOWED_UPLOAD_TYPES: Record<string, { ext: string; contentType: string }> = {
  'image/jpeg': { ext: 'jpg', contentType: 'image/jpeg' },
  'image/png':  { ext: 'png', contentType: 'image/png'  },
  'image/webp': { ext: 'webp', contentType: 'image/webp' },
  'image/gif':  { ext: 'gif', contentType: 'image/gif'  },
};
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

async function uploadImageToStorage(file: File): Promise<string> {
  const spec = ALLOWED_UPLOAD_TYPES[file.type];
  if (!spec) throw new Error(`نوع الملف مش مسموح — JPG/PNG/WebP/GIF فقط (${file.type || 'unknown'})`);
  const fileExt = (file.name.split('.').pop() || '').toLowerCase();
  if (fileExt && fileExt !== spec.ext && !(spec.ext === 'jpg' && fileExt === 'jpeg')) {
    throw new Error(`الامتداد ${fileExt} مش مطابق نوع الملف ${file.type}`);
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`حجم الصورة أكبر من ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB`);
  }
  // Batch 46 / SPECS/006: write under `home/` so the per-prefix storage
  // policy `hero_slides_home_write_*` gates this upload on
  // `has_section_access('home')` and NOT on the legacy single-gate
  // policy that would silently permit a non-home admin.
  const path = `home/${crypto.randomUUID()}.${spec.ext}`;
  const { error: upErr } = await supabase.storage.from('hero-slides').upload(path, file, {
    cacheControl: '31536000',
    contentType: spec.contentType,
    upsert: false,
  });
  if (upErr) throw upErr;
  const { data } = supabase.storage.from('hero-slides').getPublicUrl(path);
  return data.publicUrl;
}

export default function HomeSlidesPage() {
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { void load(); }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase.from('home_hero_slides').select('*').order('sort_order');
    if (err) setError(err.message);
    else setSlides((data ?? []) as HeroSlide[]);
    setLoading(false);
  };

  const resequence = async (ordered: HeroSlide[]) => {
    for (let i = 0; i < ordered.length; i++) {
      if (ordered[i].sort_order === i) continue;
      await supabase.from('home_hero_slides').update({ sort_order: i, updated_at: new Date().toISOString() }).eq('id', ordered[i].id);
    }
  };

  // One handler for both "one file" and "many files" — the input has
  // `multiple` set, so the browser lets the admin pick one or many
  // from the same dialog. If they pick one, it uploads one; if they
  // pick many, it uploads them in order.
  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setError(null);
    setUploading({ done: 0, total: files.length });
    const startOrder = slides.reduce((max, s) => Math.max(max, s.sort_order), -1) + 1;
    let inserted = 0;
    for (let i = 0; i < files.length; i += 1) {
      try {
        const url = await uploadImageToStorage(files[i]);
        const { error: insErr } = await supabase.from('home_hero_slides').insert({
          image_url: url, active: true, sort_order: startOrder + i,
        });
        if (insErr) throw insErr;
        inserted += 1;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(`فشل ${files[i]?.name}: ${msg}. ${inserted} صورة اترفعت.`);
        break;
      }
      setUploading({ done: i + 1, total: files.length });
    }
    setUploading(null);
    await load();
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= slides.length) return;
    const a = slides[index], b = slides[target];
    await supabase.from('home_hero_slides').update({ sort_order: -1 }).eq('id', a.id);
    await supabase.from('home_hero_slides').update({ sort_order: a.sort_order }).eq('id', b.id);
    await supabase.from('home_hero_slides').update({ sort_order: b.sort_order }).eq('id', a.id);
    await load();
  };

  const toggleActive = async (s: HeroSlide) => {
    const { error: err } = await supabase.from('home_hero_slides').update({ active: !s.active }).eq('id', s.id);
    if (err) setError(err.message);
    else await load();
  };

  const remove = async (id: string) => {
    if (!(await confirm({
      title: 'حذف الصورة من الكاروسيل؟',
      confirmText: 'احذف',
      tone: 'danger',
    }))) return;
    const { error: err } = await supabase.from('home_hero_slides').delete().eq('id', id);
    if (err) return setError(err.message);
    await resequence(slides.filter((s) => s.id !== id));
    await load();
  };

  const openPicker = () => {
    inputRef.current?.click();
  };

  return (
    <>
      <PageHeader
        eyebrow="الرئيسية"
        title="صور الكاروسيل"
        subtitle="الصور اللي بتظهر ف السلايدر أعلى الصفحة الرئيسية للتطبيق — كل صورة لها ترتيب مش بيتكرر."
        actions={
          <>
            {/* One input, one big button. The input is hidden and the
                button clicks it. `multiple` on the input lets the admin
                pick a single file OR a batch from the same picker. */}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                // Convert FileList → Array *before* clearing the input.
                // The FileList is a live reference to the input's own
                // files; if we clear input.value first, the FileList
                // shrinks to zero mid-loop and only the first upload
                // completes. An independent array is safe.
                const picked = e.target.files ? Array.from(e.target.files) : [];
                e.target.value = '';
                if (picked.length > 0) void handleFiles(picked);
              }}
            />
            <button className="btn" onClick={openPicker} disabled={!!uploading}>
              {uploading ? `جاري الرفع ${uploading.done} من ${uploading.total}` : '+ رفع صورة أو دفعة صور'}
            </button>
          </>
        }
      />
      {error && <div className="error-inline">{error}</div>}

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : slides.length === 0 ? (
        <div className="card empty">مفيش صور لسه — دوس على «رفع صورة أو دفعة صور» فوق.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {slides.map((s, i) => (
            <div key={s.id} className="card" style={{ display: 'flex', gap: 16, alignItems: 'center', padding: 12, opacity: s.active ? 1 : 0.55 }}>
              <div style={{
                minWidth: 44, height: 44, borderRadius: 22,
                background: 'var(--teal, #009091)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 18,
              }}>
                {i + 1}
              </div>
              <img src={s.image_url} alt="" style={{ width: 260, height: 146, objectFit: 'cover', borderRadius: 10, background: '#000' }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={s.active} onChange={() => void toggleActive(s)} />
                  <span>{s.active ? 'ظاهرة ف التطبيق' : 'مخفية'}</span>
                </label>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', direction: 'ltr' }}>
                  {s.image_url.split('/').pop()}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button className="btn btn-small btn-secondary" disabled={i === 0} onClick={() => void move(i, -1)} title="حرك للأعلى">↑</button>
                <button className="btn btn-small btn-secondary" disabled={i === slides.length - 1} onClick={() => void move(i, 1)} title="حرك للأسفل">↓</button>
              </div>
              <button className="btn btn-small btn-danger" onClick={() => void remove(s.id)}>حذف</button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
